import React, { useEffect, useState } from 'react';
import { X, Settings, Save, Loader2 } from 'lucide-react';
import { classroomApi } from '@/lib/classroomApi';
import { toast } from 'sonner';

/**
 * Phase 3A-2: Classroom Settings modal (tutor-write, student-read).
 *
 * Persisted in classroom_room_settings via PUT /settings.
 * Settings drive default layout on join + feature toggles (cursors, math).
 */
const Toggle = ({ checked, onChange, label, hint, disabled, testid }) => (
  <label className={`flex items-start justify-between gap-4 py-3 border-b border-gray-100 last:border-b-0 ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
    <div className="flex-1">
      <div className="text-sm font-semibold text-gray-900">{label}</div>
      {hint && <div className="text-xs text-gray-500 mt-0.5">{hint}</div>}
    </div>
    <button
      type="button" disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${checked ? 'bg-red-700' : 'bg-gray-300'}`}
      data-testid={testid}
    >
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-5' : 'translate-x-0.5'} mt-0.5`} />
    </button>
  </label>
);

const ClassroomSettingsPanel = ({ open, onClose, roomId, token, role, settings, onChange }) => {
  const [draft, setDraft] = useState(settings);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(settings); }, [settings, open]);

  if (!open) return null;
  const isTutor = role === 'tutor';

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await classroomApi.settingsUpdate(roomId, token, draft);
      onChange(r.settings);
      toast.success('Settings saved');
      onClose();
    } catch (e) { toast.error(e.message || 'Could not save settings'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
         data-testid="classroom-settings-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Settings className="h-4 w-4 text-red-700" />
            <div className="text-sm font-bold uppercase tracking-wider">Classroom Settings</div>
          </div>
          <button onClick={onClose} className="h-7 w-7 rounded-lg hover:bg-gray-100 flex items-center justify-center"
                  data-testid="classroom-settings-close"><X className="h-4 w-4" /></button>
        </div>

        <div className="px-5 py-2">
          <div className="py-3 border-b border-gray-100">
            <div className="text-sm font-semibold text-gray-900 mb-2">Default layout on join</div>
            <div className="grid grid-cols-3 gap-1.5 bg-gray-100 p-1 rounded-lg">
              {['split', 'whiteboard', 'document'].map((l) => (
                <button key={l} onClick={() => isTutor && set('default_layout', l)} disabled={!isTutor}
                  className={`h-9 text-[11px] uppercase tracking-wider font-bold rounded-md transition ${draft.default_layout === l ? 'bg-white text-black shadow' : 'text-gray-600 hover:text-black'} ${!isTutor ? 'cursor-not-allowed' : ''}`}
                  data-testid={`settings-layout-${l}`}>{l}</button>
              ))}
            </div>
          </div>

          <Toggle checked={!!draft.cursors_enabled} onChange={(v) => set('cursors_enabled', v)}
            label="Live cursor presence" hint="Show your partner's cursor over the classroom in real time."
            disabled={!isTutor} testid="settings-toggle-cursors" />

          <Toggle checked={!!draft.math_enabled} onChange={(v) => set('math_enabled', v)}
            label="Math notes (KaTeX)" hint="LaTeX-rendered math note pad in the split layout."
            disabled={!isTutor} testid="settings-toggle-math" />

          <Toggle checked={!!draft.allow_student_draw_on_pdf} onChange={(v) => set('allow_student_draw_on_pdf', v)}
            label="Student can annotate PDFs" hint="When off, only tutor can draw on shared documents."
            disabled={!isTutor} testid="settings-toggle-stu-draw" />
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50 rounded-b-2xl">
          <div className="text-[10px] text-gray-500 uppercase tracking-wider">{isTutor ? 'Tutor controls' : 'Read-only · ask your tutor to change'}</div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="h-9 px-4 rounded-full bg-white border border-gray-200 text-xs font-bold uppercase tracking-wider hover:bg-gray-100"
                    data-testid="classroom-settings-cancel">Close</button>
            {isTutor && (
              <button onClick={save} disabled={saving}
                className="h-9 px-4 rounded-full bg-red-700 hover:bg-red-800 text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-40"
                data-testid="classroom-settings-save">
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassroomSettingsPanel;
